import { Component, DestroyRef, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  TalkdeskChatSDK,
  TalkDeskState,
} from '@data-access/talk-desk/types/talkdesk.type';
import { Actions, ofType } from '@ngrx/effects';
import { map } from 'rxjs';
import { User } from '@iris-sites/interfaces/sites.interfaces';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AuthService } from '@auth/auth.service';
import {
  TALKDESK_ACTIONS,
  initializeTalkdeskScript,
  mapContextParams,
} from '@data-access/talk-desk';

@Component({
  imports: [],
  selector: 'wcg-talkdesk',
  styleUrl: './talkdesk.component.css',
  template: ``,
})
export class TalkdeskComponent {
  private readonly _store = inject(Store<TalkDeskState>);
  private readonly _actions$ = inject(Actions);
  private readonly _authService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);

  private static initialized = false;
  private user: User = {} as User;

  constructor() {
    if (!TalkdeskComponent.initialized) {
      this.initialize();
      this._customListeners();
      TalkdeskComponent.initialized = true;
    }
  }

  private _customListeners() {
    this._actions$
      .pipe(
        ofType(TALKDESK_ACTIONS.openChat),
        takeUntilDestroyed(this._destroyRef),
        map((actionData) => {
          const { chatId, chatData } = actionData;
          // Get the webchat instance from the window
          const webchat = window.webchat as TalkdeskChatSDK['prototype'];
          if (webchat) {
            try {
              webchat.setContextParam({
                field_option1: chatData.question ?? '',
              });

              if (chatData) {
                const contextParams = mapContextParams(chatData);
                webchat.setContextParam(contextParams);
              } else {
                // Fallback if no context is found
                webchat.setContextParam({ chatId });
              }

              // Open the chat - this makes the widget visible but preserves the initial screen
              webchat.open();
            } catch (error: unknown) {
              // Just log the error
              console.error('Error opening Talkdesk chat:', error);
              throw new Error(error as string);
            }
          } else {
            this._store.dispatch(
              TALKDESK_ACTIONS.initializeFailure({
                error: 'Webchat SDK not initialized',
              }),
            );
          }
        }),
      )
      .subscribe();
  }

  private initialize() {
    this._authService.user$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((userDetails: any) => {
        try {
          this.user = userDetails as unknown as User;
          initializeTalkdeskScript(this.user, (error) =>
            this._store.dispatch(TALKDESK_ACTIONS.initializeFailure({ error })),
          );
          this._store.dispatch(TALKDESK_ACTIONS.initializeSuccess());
        } catch (error) {
          const errorMessage =
            error instanceof Error
              ? error.message
              : 'Unknown error initializing Talkdesk';
          this._store.dispatch(
            TALKDESK_ACTIONS.initializeFailure({ error: errorMessage }),
          );
        }
      });
  }
}
